import { Button } from "@svea/design-system-components-react";
import {
	type AnyFunction,
	ButtonGroup,
	Dialog,
	formatPhone,
	isRequired,
	P,
	type Phone,
	ReactSelectField,
	Stack,
	Text,
	TextField,
	useText,
	validateEmail,
	validatePhoneNumber,
	validateUniqueString,
} from "@sveabank/cc-common";
import { useEffect, useRef, useState } from "react";
import { Form } from "react-final-form";
import Paper from "../../../../components/paper";
import { usePageContext } from "../../../../hooks/context";
import { useApplicationId } from "../../../../hooks/useApplicationId";
import { useLazyFetchClcPersonDataQuery } from "../../../../services/clc-application";
import type { ClcPerson, ClcShareholder } from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";

type Props = {
	open: boolean;
	setOpen: (isOpen: boolean) => void;
	shareholders: ClcShareholder[];
	saveShareholder: (shareholder: ClcShareholder) => void;
};

type ShareholderForm = {
	email: string;
	phone: Phone;
};

type FetchPersonForm = {
	nationalId: string;
};

export const AddShareholderModal = ({ open, setOpen, shareholders, saveShareholder }: Props) => {
	const [t, texts] = useText();
	const applicationId = useApplicationId();

	// needed for external form submit:
	const submitShareholderRef = useRef<AnyFunction | null>(null);

	const { countryCodes } = usePageContext(useClcCompanyApplication);

	const [fetchPersonData, { isFetching: isFetchingPerson }] = useLazyFetchClcPersonDataQuery();

	const [person, setPerson] = useState<ClcPerson | null>(null);

	// reset form on modal close/open:
	useEffect(() => {
		setPerson(null);
	}, [open]);

	const onSubmitShareholder = (person: ClcPerson) => (values: ShareholderForm) => {
		const shareholder: ClcShareholder = {
			...person,
			email: values.email,
			numberOfShares: 0,
			phone: {
				countryCode: values.phone.countryCode,
				number: formatPhone(values.phone.number),
			},
		};
		saveShareholder(shareholder);
		setOpen(false);
	};

	const onSubmitFetchPerson = async ({ nationalId }: FetchPersonForm) => {
		setPerson(null);
		const person = await fetchPersonData({
			applicationId,
			nationalId,
		}).unwrap();
		setPerson(person);
	};

	return (
		<Dialog
			isOpen={open}
			setOpen={setOpen}
			center
			size="small"
			heading={t(texts.newCompany.shares.addShareholderModalHeading)}
		>
			<Dialog.Content>
				<Stack gap={4}>
					<Stack gap={2}>
						<Text align="start">{t(texts.newCompany.shares.addShareholderModalText)}</Text>
						<Form<FetchPersonForm>
							onSubmit={onSubmitFetchPerson}
							render={({ handleSubmit }) => (
								<form onSubmit={handleSubmit}>
									<Stack gap={2} fullSize>
										<TextField
											name="nationalId"
											label={t(texts.newCompany.nationalId)}
											validators={[
												isRequired,
												validateUniqueString(
													shareholders.map((shareholder) => shareholder.nationalId),
													texts.validation.personAlreadyAdded,
												),
											]}
											fullWidth
										/>
										<Button
											variant={person ? "secondary" : "primary"}
											width="full"
											type="submit"
											isPending={isFetchingPerson}
										>
											{t(texts.newCompany.getPerson)}
										</Button>
									</Stack>
								</form>
							)}
						/>
					</Stack>
					{!!person && (
						<>
							<Paper className="bg-background-tertiary">
								<Text align="left">
									<P>
										<Text bold>
											{person.firstName} {person.lastName}
										</Text>
									</P>
									<P>{person.postalAddress}</P>
									<P>
										{person.postCode} {person.town}
									</P>
								</Text>
							</Paper>
							<Form<ShareholderForm>
								onSubmit={onSubmitShareholder(person)}
								render={({ handleSubmit }) => {
									// needed for external form submit:
									submitShareholderRef.current = handleSubmit;

									const existingPhoneNumbers = shareholders.map((s) => s.phone.number);
									const existingEmails = shareholders.map((s) => s.email || "");

									return (
										<form onSubmit={handleSubmit}>
											<Stack gap={2}>
												<Stack direction="row" align="start" gap={2}>
													<ReactSelectField
														label={t(texts.contactInfo.companyContact.countryCode)}
														floatingLabel
														name="phone.countryCode"
														options={countryCodes}
														placeholder={t(texts.common.choose)}
														initialValue="+46"
														validators={[isRequired]}
														menuPortalTarget={null}
													/>

													<TextField
														fullWidth
														label={t(texts.newCompany.companyInfo.phone)}
														name="phone.number"
														validators={[
															isRequired,
															validatePhoneNumber,
															validateUniqueString(
																existingPhoneNumbers,
																texts.validation.phoneAlreadyAdded,
															),
														]}
													/>
												</Stack>
												<TextField
													fullWidth
													label={t(texts.newCompany.companyInfo.email)}
													name="email"
													validators={[
														isRequired,
														validateEmail,
														validateUniqueString(
															existingEmails,
															texts.validation.emailAlreadyAdded,
														),
													]}
												/>
											</Stack>
										</form>
									);
								}}
							/>
						</>
					)}
				</Stack>
			</Dialog.Content>
			<Dialog.Footer>
				<ButtonGroup>
					{!!person && (
						<Button
							onPress={() => {
								submitShareholderRef.current?.();
							}}
						>
							{t(texts.common.save)}
						</Button>
					)}
					<Button variant="secondary" onPress={() => setOpen(false)}>
						{t(texts.common.abort)}
					</Button>
				</ButtonGroup>
			</Dialog.Footer>
		</Dialog>
	);
};
